import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";
import useSeo from "@/hooks/useSeo";

export default function Terms() {
  useSeo({
    title: "Terms of Service — WebJesters",
    description:
      "WebJesters' plainspoken terms covering managed websites, subscriptions, domains, content ownership, and limitations — written for blue-collar service businesses.",
    path: "/terms",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Terms of Service", item: "https://webjesters.com/terms" },
      ],
    },
  });
  return (
    <LegalLayout
      title="Terms of Service"
      eyebrow="Legal"
      lastUpdated="October 16, 2026"
    >
      <p>
        These Terms of Service ("Terms") are a binding agreement between you
        and WebJesters LLC, an Arizona limited liability company
        ("WebJesters," "we," "us," or "our"). They apply when you visit
        webjesters.com, submit an inquiry, reserve a build, buy a one-time
        service, or use any of our hosted services. By doing any of those
        things you accept these Terms. We've kept them short and plainspoken
        on purpose.
      </p>
      <p>
        <strong className="text-white">Please read the "Dispute resolution" section.</strong>{" "}
        It requires most disputes to be resolved by individual binding
        arbitration rather than in court, and it waives class actions and jury
        trials. You can opt out within 30 days as described there.
      </p>

      <LegalSection title="Who can use our services">
        <p>
          Our services are for businesses. You must be at least 18, and if you
          are acting for a company you confirm you have authority to bind it
          to these Terms. You are responsible for keeping your contact details
          current and for anyone you let act on your account.
        </p>
      </LegalSection>

      <LegalSection title="What WebJesters is">
        <p>
          WebJesters is a managed website platform for local service
          businesses. We build your site and we run the infrastructure behind
          it — hosting, SSL, domain setup, deployments, security, and
          ongoing maintenance — so you don't have to think about it.
        </p>
        <p>
          Our standard package is{" "}
          <strong className="text-white">$499</strong> to build plus{" "}
          <strong className="text-white">$79/month</strong> to host and
          manage. A <strong className="text-white">$99 deposit</strong>{" "}
          reserves your build slot and is credited toward the $499 build fee.
          The remaining build balance is due before launch. The $79/month
          begins when your site goes live and is billed month-to-month.
        </p>
        <p>
          WebJesters also offers a one-time{" "}
          <strong className="text-white">Google Business Profile setup or cleanup</strong>{" "}
          service and a separate, independently billed{" "}
          <strong className="text-white">Local Visibility</strong> service,
          each described in its own section below, plus optional quote and call
          automations and improvements to websites we did not build. None of
          these require purchasing a website from WebJesters.
        </p>
      </LegalSection>

      <LegalSection title="What's included each month">
        <p>Your monthly subscription covers:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Hosting, SSL, and domain setup</li>
          <li>Security patches, backups, and uptime monitoring</li>
          <li>
            Your private client portal, including lead tracking for every
            quote and contact request. Portal access is included only when
            WebJesters builds your website
          </li>
          <li>
            Self-service updates — you can change your business information,
            hours, services, and photos in the portal yourself, and those
            changes update your website
          </li>
          <li>
            Lead alerts for quote and contact requests, sent to your email
          </li>
          <li>
            Reasonable minor ongoing updates — swapping photos, refreshing
            copy, updating service areas or hours, small content changes
          </li>
        </ul>
        <p>
          <strong className="text-white">
            Optional quote &amp; call automations.
          </strong>{" "}
          Three separate automations are available, and none of them are
          included in the plans above:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>
            <strong className="text-white">Text alerts to you</strong> when a
            new quote request arrives on your website
          </li>
          <li>
            <strong className="text-white">
              Automatic email and text acknowledgments to your customer
            </strong>{" "}
            confirming that their request was received
          </li>
          <li>
            <strong className="text-white">Missed-call text backs</strong>{" "}
            sent to a caller when you can't answer
          </li>
        </ul>
        <p>
          Acknowledgments confirm receipt only. They do{" "}
          <strong className="text-white">not</strong> calculate or send a price
          and do not book or confirm an appointment — that is separate
          functionality that would be scoped, agreed, and quoted on its own.
        </p>
        <p>
          Each automation is optional, selected individually, and quoted
          individually after we check whether your website's forms and your
          phone setup can support it. They can be installed on a
          WebJesters-built site or on a compatible existing website, and nothing
          is turned on until you've agreed to the scope and the price.
        </p>
        <p>
          <strong className="text-white">
            Improving an existing website.
          </strong>{" "}
          If you already have a website, we can improve it instead of building
          a new one — design refreshes, mobile layout fixes, new service pages
          or galleries, content updates, and better contact or quote forms. The
          initial review of your website is free and carries no obligation. We
          check the platform your site is built on first and tell you whether
          it can be improved in place or would need a rebuild. Each project
          then receives a flat quote based on the agreed scope. A one-time
          project is sufficient — ongoing management is optional and agreed
          separately. The $499 build plus $79/month plan described above
          remains its own separate offer.
        </p>
        <p>
          Larger work — new pages, structural redesigns, custom integrations,
          or significant content rewrites — sits outside the monthly plan
          and is quoted separately before any work begins. We'll always
          confirm scope and price with you first.
        </p>
      </LegalSection>

      <LegalSection title="Subscription &amp; access">
        <p>
          Hosting, the client portal, and ongoing management stay active
          while your subscription is current. If a payment fails, we'll reach
          out by email and SMS to get it sorted.
        </p>
        <p>
          If an account stays unpaid after a reasonable grace period, we may
          temporarily pause hosting until things are caught up. We'd rather
          not — it's only there for accounts that go quiet entirely.
        </p>
      </LegalSection>

      <LegalSection title="Domains">
        <p>
          When WebJesters registers a domain on your behalf, we manage it for
          you as part of the service — renewals, DNS, and email routing
          stay handled.
        </p>
        <p>
          The domain is associated with your business and can be transferred
          to you upon request once your account is settled — just email us
          and we'll move it to the registrar of your choice. No drama, no
          holdouts.
        </p>
      </LegalSection>

      <LegalSection title="Your content, our platform">
        <p>
          You own the content you give us — your business name, logo, photos,
          and copy. You're responsible for making sure you have the right to
          use anything you upload (photos of your work, customer reviews,
          third-party logos, etc.).
        </p>
        <p>
          The website is built and runs on WebJesters' hosting and platform
          infrastructure. The underlying code, design system, deployment
          tooling, and platform features are ours. While you're a WebJesters
          subscriber, all of it is fully at your service. After cancellation,
          we'll provide an export of your text and image content on request.
          The underlying website code and platform are not included in that
          export.
        </p>
        <p>
          <strong className="text-white">License to us.</strong> You give us
          a non-exclusive license to host, copy, display, and adapt your
          content as needed to build and run your services, including resizing
          photos and preparing Local Visibility posts. It ends when the related
          service ends, except for backups kept under our Privacy Policy.
        </p>
        <p data-testid="terms-portfolio-rights">
          <strong className="text-white">Portfolio use.</strong> We may show
          work we've done for you — your site, screenshots, and your business
          name and logo — in our portfolio, proposals, and social posts, with
          a link to your live site. Email us and we'll remove it within ten
          business days. We never publish your customers' personal data or
          your leads.
        </p>
      </LegalSection>

      <LegalSection title="Initial build scope and revisions">
        <p>
          The standard <strong className="text-white">$499 build</strong>{" "}
          covers up to five standard pages, with the exact page list and scope
          agreed before work begins. A one-page website is also fine when it
          suits the business.
        </p>
        <p>
          The build includes{" "}
          <strong className="text-white">two consolidated revision rounds</strong>{" "}
          before launch. Corrections to errors in our own work do not count
          toward those rounds.
        </p>
        <p>
          Additional pages, major changes to the approved direction, advanced
          features, and custom integrations are quoted separately before we
          proceed.
        </p>
      </LegalSection>

      <LegalSection title="Google Business Profile setup or cleanup (one-time)">
        <p>
          This is a standalone, one-time service priced at{" "}
          <strong className="text-white">$299 per business location</strong>.
          No monthly subscription is created and no website purchase is
          required. Payment is collected up front through Stripe before work
          begins.
        </p>
        <p>
          It covers either creating a new Google Business Profile or auditing,
          cleaning up, and completing an existing one, including: business
          details, hours, contact information, website links, and service
          areas; primary and secondary category research and selection;
          business description and services; uploading the logo, cover image,
          and photos you provide; a review-request link; verification guidance
          when Google requires it; and a brief completion summary.
        </p>
        <p>
          <strong className="text-white">Ownership and access.</strong> You
          retain ownership of your Google Business Profile and grant WebJesters
          manager access from your own account. We never request your Google
          password, and you can remove our access at any time.
        </p>
        <p>
          <strong className="text-white">Verification and outcomes.</strong>{" "}
          Verification may require your participation and is decided by
          Google. WebJesters does not guarantee verification, rankings, or
          leads. Suspension appeals, ownership disputes, and complicated
          duplicate-listing issues are outside this service and require a
          separate assessment and quote.
        </p>
        <p>
          <strong className="text-white">Refunds.</strong> The $299 fee is
          refundable before work begins and becomes non-refundable once setup
          or cleanup has started.
        </p>
      </LegalSection>

      <LegalSection title="Local Visibility (optional)">
        <p>
          Local Visibility is its own separate service that keeps your
          Google Business Profile active and your social media presence
          consistent. It is{" "}
          <strong className="text-white">
            not part of the website plan
          </strong>{" "}
          and is billed separately. No website purchase is required: you can
          use it alongside a WebJesters website, alongside a website hosted
          elsewhere, or with no website at all. Ending it does not affect your
          website subscription, and ending a website subscription does not
          affect it.
        </p>
        <p>
          Pricing is{" "}
          <strong className="text-white">$149 one-time onboarding</strong> for
          initial Google Business Profile setup or cleanup, then{" "}
          <strong className="text-white">$299/month</strong> for publishing to
          your Google Business Profile and one included social platform
          (a Facebook Page or an Instagram professional account). Adding the
          other platform costs{" "}
          <strong className="text-white">$49/month</strong>, for a total of
          $348/month. The onboarding fee is collected before work begins. The
          monthly service starts once setup is complete and we have the
          account access and content needed to begin posting, and is billed
          month-to-month.
        </p>
        <p>
          <strong className="text-white">Onboarding-fee waiver.</strong> If you
          have already completed and paid for our standalone $299 Google
          Business Profile setup, the $149 onboarding fee is waived when you
          add Local Visibility, provided substantial new setup work is not
          required. Eligibility is confirmed by WebJesters against our payment
          records; it is not self-selected.
        </p>
        <p>Each month, the monthly fee covers:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>
            Four content pieces, created from photos or clips you provide
          </li>
          <li>
            Publishing each piece to both your Google Business Profile and
            your included social platform. A piece shared on both platforms
            counts as one of the four, so the plan provides up to four posts
            on each platform — eight placements in total, not four split
            between them
          </li>
          <li>Basic profile upkeep</li>
          <li>
            A short monthly email summary listing the posts we published with
            links, the profile information we updated, and any content or
            approvals we need from you for next month
          </li>
        </ul>
        <p>
          <strong className="text-white">Additional social platform.</strong>{" "}
          The additional platform receives adapted versions of the same four
          monthly content pieces, adapted for that platform — appropriate
          captions, sizing, scheduling, and publishing. It does not include four additional
          unique content pieces. We only charge for the additional platform
          once your account access is ready and delivery can begin.
        </p>
        <p>
          <strong className="text-white">Scope.</strong> You supply photos and
          clips; we prepare them for posting, with captions, simple branding,
          and light edits where needed, then schedule and publish the content.
          Depending on the material, light content preparation may include a
          simple text overlay, your logo, cropping, a basic before-and-after
          layout, or trimming a short video. We apply these where appropriate;
          not every post receives graphics or editing. Each prepared piece is
          adapted for every included platform. We preserve your original
          uploaded media and make any edits or platform-compatible versions
          on copies, without replacing the originals.
        </p>
        <p>
          <strong className="text-white">Creative limits.</strong> Light
          content preparation does not include elaborate custom designs,
          extensive video editing, motion graphics or animation, or multiple
          creative versions of a piece. Sample posts shown on our website
          illustrate possible treatments and are not the required standard
          for every post.
        </p>
        <p>
          <strong className="text-white">Not included:</strong> filming or
          photo shoots, comment or inbox management, review replies, paid
          advertising, separate original content campaigns for each platform,
          elaborate custom design or extensive video editing, and client
          portal access (the portal is included only with a
          WebJesters-built website). Anything outside this scope would be a
          separate conversation and a separate agreement.
        </p>
        <p>
          <strong className="text-white">Sending us content.</strong> You can
          send photos, clips, and business updates by text or email. To
          preserve photo and video quality we ask for original files by email
          or a download link (such as Google Drive, Dropbox, or iCloud), since
          regular text attachments are usually compressed.
        </p>
        <p>
          <strong className="text-white">Approval before publishing.</strong>{" "}
          We prepare each post and send it to you for approval before it goes
          live on your Google Business Profile or social platform.
        </p>
        <p>
          <strong className="text-white">Unused monthly posts.</strong> If we
          don't have the content or approvals needed to publish, those content
          pieces do not roll over into the following month. If WebJesters misses
          a post that was included in your month, that work remains owed to
          you.
        </p>
        <p>
          <strong className="text-white">Onboarding fee &amp; verification.</strong>{" "}
          The $149 onboarding fee is refundable before work begins and becomes
          non-refundable once setup or cleanup has started. Google approval or
          verification of your profile is decided by Google and is not
          guaranteed.
        </p>
        <p>
          <strong className="text-white">Adding, switching, or removing platforms.</strong>{" "}
          Platform additions, switches, and removals take effect starting with
          the next billing month. For additions or switches, you must give us
          the manager access we need before that month begins. Instagram
          publishing requires a professional account connected to a Facebook
          Page.
        </p>
        <p>
          <strong className="text-white">Your content, your accounts.</strong>{" "}
          Content is created from material you send us, so you're
          responsible for having the right to use it and for the accuracy of
          the business information you provide. You keep ownership of your
          Google Business Profile and social accounts; WebJesters works through
          manager access that you grant and can remove at any time.
        </p>
        <p>
          Visibility depends on factors outside anyone's control, including
          platform algorithms, competition, and seasonality. WebJesters does
          not guarantee rankings, review counts, follower growth, lead
          volume, or any specific business outcome, and we're not responsible
          for platform policy changes, suspensions, or outages.
        </p>
        <p>
          You can cancel Local Visibility any time by emailing{" "}
          <a
            href="mailto:team@webjesters.com"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>
          . Cancellation takes effect at the end of your current billing
          cycle.
        </p>
      </LegalSection>

      <LegalSection title="Payments &amp; refunds" id="refunds">
        <p>
          All payments run securely through Stripe. We don't store your card
          details. Monthly fees are charged automatically through Stripe on a
          recurring basis.
        </p>
        <p>
          <strong className="text-white">Website plan.</strong> The $99
          deposit is credited toward your $499 build fee and is fully
          refundable if we can't deliver a site you'd be proud to publish.
          Once your site is live and approved, the remaining build balance and
          monthly fees are non-refundable, but you can cancel future months
          anytime. Monthly billing starts when your website goes live.
        </p>
        <p>
          <strong className="text-white">Google Business Profile setup.</strong>{" "}
          The $299 one-time fee is collected before work begins, creates no
          subscription, and is refundable until we start the setup or cleanup.
        </p>
        <p>
          <strong className="text-white">Local Visibility.</strong> The $149
          onboarding fee is collected before work begins and is refundable up
          until we start the setup or cleanup. The monthly fee ($299, or $348
          with a second social platform) begins once setup is complete and we
          have the access and content needed to start posting, and is
          non-refundable for months already billed. Existing subscriptions keep
          their current pricing and platforms unless you ask us to change them.
        </p>
        <p>
          <strong className="text-white">Chargebacks.</strong> If you have a
          billing concern, email us first — we resolve almost everything within
          a business day. Disputing a charge with your card issuer for a
          service that was delivered, or while a refund request is in progress,
          may result in suspension of hosted services until it is resolved.
          Taxes, if any apply, are added at checkout.
        </p>
      </LegalSection>

      <LegalSection title="Hours &amp; timing">
        <p>
          WebJesters' business hours are Monday through Friday, 9:00 a.m. to
          5:00 p.m. Arizona time. We respond to inquiries within one business
          day.
        </p>
        <p>
          Most initial website drafts are ready within a few business days
          after we receive the information and content we need from you.
          Launch timing depends on revisions and your approval, so we don't
          promise a fixed launch date.
        </p>
      </LegalSection>

      <LegalSection title="SMS messaging">
        <p>
          When you submit any of our inquiry forms and consent to texts, you
          agree to receive SMS communications from WebJesters related to your
          inquiry, quote, project, and account.
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li data-testid="terms-opt-out">
            <strong className="text-white">Opt-out:</strong> Reply STOP to
            cancel.
          </li>
          <li>
            <strong className="text-white">Help:</strong> Reply HELP for
            support, or email{" "}
            <a
              href="mailto:team@webjesters.com"
              className="text-white underline underline-offset-4 hover:text-zinc-100"
            >
              team@webjesters.com
            </a>
            .
          </li>
          <li data-testid="terms-msg-rates">
            Message and data rates may apply. Message frequency varies.
          </li>
          <li>
            Supported carriers include but are not limited to AT&amp;T,
            Verizon, T-Mobile, and other major U.S. carriers. Carriers are
            not liable for delayed or undelivered messages.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Acceptable use">
        <p>You agree not to use WebJesters services to:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Publish illegal, defamatory, or fraudulent content</li>
          <li>Send spam or unsolicited communications</li>
          <li>Infringe on third-party intellectual property</li>
          <li>Attempt to disrupt or reverse-engineer our systems</li>
        </ul>
        <p>
          We reserve the right to suspend or end service for clear violations
          of this section.
        </p>
      </LegalSection>

      <LegalSection title="Cancellation">
        <p>
          You can cancel monthly hosting any time by emailing{" "}
          <a
            href="mailto:team@webjesters.com"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>
          . Cancellation takes effect at the end of your current billing
          cycle. After cancellation we'll deactivate the hosted site and keep
          a backup for 30 days in case you change your mind. Your text and
          image content can be exported on request, and domains we manage on
          your behalf can be transferred to you once the account is settled.
          The underlying website code and platform are not included.
        </p>
      </LegalSection>

      <LegalSection title="Force majeure">
        <p>
          WebJesters will not be liable for delays or failures in performing
          its obligations caused by events beyond our reasonable control,
          including internet outages, cloud provider outages, natural
          disasters, labor disputes, governmental actions, or other force
          majeure events.
        </p>
      </LegalSection>

      <LegalSection title="Third-party platforms">
        <p>
          Parts of our services run on platforms we don't control — Google
          (Search, Business Profile, Ads), Meta (Facebook, Instagram), domain
          registrars, Stripe, and cloud and messaging providers. Those
          companies set their own rules and can change features, require
          verification, limit reach, or suspend a profile at any time. We will
          follow their published rules and help you work through issues, but
          we are not responsible for their decisions, outages, or policy
          changes, and such events are not grounds for a refund of work
          already performed.
        </p>
      </LegalSection>

      <LegalSection title="Disclaimer of warranties">
        <p>
          Our services are provided "as is" and "as available." To the fullest
          extent permitted by law we disclaim all warranties, express or
          implied, including merchantability, fitness for a particular
          purpose, and non-infringement. We do not guarantee search engine
          rankings, Google Business Profile verification or approval, lead
          volume, revenue, business growth, or uninterrupted, error-free
          service. Any advice we give about marketing or your business is
          general information, not professional, legal, or financial advice.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the extent permitted by law, WebJesters and its members,
          employees, and contractors will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for lost
          profits, revenue, data, or goodwill, even if we were told they were
          possible. Our total liability for all claims relating to these Terms
          or our services is limited to the amount you paid us in the three
          months before the event giving rise to the claim. Some states don't
          allow some of these limits, so they may not fully apply to you.
        </p>
      </LegalSection>

      <LegalSection title="Indemnification">
        <p>
          You agree to defend and hold WebJesters harmless from claims,
          losses, and reasonable legal fees arising from content you provide,
          your business's products or services, your violation of these Terms
          or any law, or your infringement of someone else's rights. We'll
          notify you promptly of any such claim and let you lead the defense,
          provided you don't settle anything that admits fault on our behalf
          without our written consent.
        </p>
      </LegalSection>

      <LegalSection title="Copyright complaints" id="dmca">
        <p>
          We respect intellectual property. If you believe content on a site
          we host infringes your copyright, send a notice to{" "}
          <a href="mailto:team@webjesters.com?subject=Copyright%20notice" className="text-white underline underline-offset-4 hover:text-zinc-100">
            team@webjesters.com
          </a>{" "}
          with: your contact details; the work you say is infringed; the exact
          URL of the material; a statement that you have a good-faith belief
          the use is unauthorized; a statement, under penalty of perjury, that
          the notice is accurate and you are authorized to act; and your
          physical or electronic signature. We may remove or disable access to
          the material and forward your notice to the client, who may respond
          with a counter-notice.
        </p>
      </LegalSection>

      <LegalSection title="Electronic communications &amp; signatures">
        <p>
          You agree that we can communicate with you electronically — by email,
          text (where you've consented), and notices in our client portal —
          and that those communications satisfy any legal requirement that a
          communication be in writing. Clicking to accept, paying through
          Stripe, or replying "approved" to a draft counts as your electronic
          signature.
        </p>
      </LegalSection>

      <LegalSection title="Dispute resolution &amp; arbitration" id="disputes">
        <p>
          <strong className="text-white">Talk to us first.</strong> If you
          have a dispute, email{" "}
          <a href="mailto:team@webjesters.com" className="text-white underline underline-offset-4 hover:text-zinc-100">team@webjesters.com</a>{" "}
          with a description and what you'd like us to do. We'll do the same
          if we have a dispute with you. Both sides agree to try in good faith
          to resolve it informally for 30 days before starting arbitration.
        </p>
        <p data-testid="terms-arbitration">
          <strong className="text-white">Binding arbitration.</strong> Any
          dispute, claim, or controversy arising out of or relating to these
          Terms or our services that isn't resolved informally will be settled
          by binding individual arbitration administered by the American
          Arbitration Association ("AAA") under its Consumer Arbitration Rules
          or, where applicable, its Commercial Arbitration Rules. The Federal
          Arbitration Act governs this section. The arbitration will take
          place in Maricopa County, Arizona, or by video or phone if either
          side asks. The arbitrator can award the same relief a court could,
          but only to the individual party seeking it. Judgment on the award
          may be entered in any court with jurisdiction.
        </p>
        <p>
          <strong className="text-white">Small claims.</strong> Either side
          may instead bring an individual claim in small-claims court in
          Maricopa County, Arizona, if it qualifies.
        </p>
        <p data-testid="terms-class-waiver">
          <strong className="text-white">No class actions; jury waiver.</strong>{" "}
          Each side may bring claims only in its individual capacity, not as a
          plaintiff or class member in any class, consolidated, or
          representative proceeding, and the arbitrator may not consolidate
          claims of more than one person. To the extent a claim proceeds in
          court, both sides waive trial by jury. If this class-action waiver
          is found unenforceable for a particular claim, that claim (and only
          that claim) will proceed in court under the "Governing law &amp;
          venue" section.
        </p>
        <p data-testid="terms-arbitration-optout">
          <strong className="text-white">30-day opt-out.</strong> You may opt
          out of arbitration by emailing{" "}
          <a href="mailto:team@webjesters.com?subject=Arbitration%20opt-out" className="text-white underline underline-offset-4 hover:text-zinc-100">team@webjesters.com</a>{" "}
          with your name, business name, and a clear statement that you opt
          out, within 30 days of first accepting these Terms. Opting out does
          not affect any other part of these Terms.
        </p>
        <p>
          <strong className="text-white">Time limit.</strong> Any claim must
          be started within one year after it arose, or it is permanently
          barred, unless the law in your state requires a longer period.
        </p>
      </LegalSection>

      <LegalSection title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will
          be sent by email at least 14 days before they take effect.
          Continued use of our services after the effective date means you
          accept the updated version.
        </p>
      </LegalSection>

      <LegalSection title="Governing law &amp; venue">
        <p>
          These Terms are governed by the laws of the State of Arizona and the
          Federal Arbitration Act, without regard to conflict-of-law rules. For
          any matter not subject to arbitration, you and WebJesters consent to
          the exclusive jurisdiction of the state and federal courts located in
          Maricopa County, Arizona.
        </p>
      </LegalSection>

      <LegalSection title="Severability">
        <p>
          If any provision of these Terms is found to be unenforceable, the
          remaining provisions will continue in full force and effect.
        </p>
      </LegalSection>

      <LegalSection title="General terms">
        <p>
          <strong className="text-white">Entire agreement.</strong> These
          Terms, together with our Privacy Policy, Cookie Policy, and any
          written quote or order you accept, are the entire agreement between
          you and WebJesters and replace any earlier discussions. If a signed
          quote conflicts with these Terms, the quote controls for that
          project only.
        </p>
        <p>
          <strong className="text-white">Assignment.</strong> You may not
          transfer these Terms without our written consent. We may assign them
          to a successor that takes over our business, and we'll notify you if
          we do.
        </p>
        <p>
          <strong className="text-white">No waiver.</strong> If we don't
          enforce a provision right away, we haven't given up the right to
          enforce it later.
        </p>
        <p>
          <strong className="text-white">Survival.</strong> Sections on
          payments, content and intellectual property, disclaimers,
          limitation of liability, indemnification, dispute resolution, and
          governing law survive the end of our relationship.
        </p>
        <p>
          <strong className="text-white">Notices.</strong> Legal notices to
          WebJesters must be sent by email to team@webjesters.com. We'll send
          legal notices to the email address on your account.
        </p>
      </LegalSection>

      <LegalSection title="Support contact">
        <p data-testid="terms-support-contact">
          Questions, account issues, or anything else? Email us at{" "}
          <a
            href="mailto:team@webjesters.com"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>
          . We typically reply within one business day.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
